<template>
  <div class="foodleap-transitions antialiased">

    <!-- Hero -->
    <header class="ft-hero relative pt-32 pb-16 overflow-hidden">
      <div class="max-w-5xl mx-auto px-8 text-center relative z-10">
        <span class="ft-badge tw-inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-6 animate-on-scroll">
          FOOD SYSTEM TRANSITIONS
        </span>
        <h1 class="text-5xl md:text-7xl font-bold tracking-tight text-primary-slate mb-6 leading-[1.1] animate-on-scroll delay-1 mt-6">
          Navigate the four<br/>food transitions
        </h1>
        <p class="text-xl text-primary-slate/60 font-light max-w-2xl mx-auto animate-on-scroll delay-2">
          The food system is shifting along four distinct axes. Explore the organisations, infrastructure, and capital driving each one — and add your ecosystem to the right map.
        </p>
      </div>
      <div class="ft-blob absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-20"></div>
      <div class="ft-blob-2 absolute -bottom-24 -left-24 w-80 h-80 rounded-full blur-3xl opacity-15"></div>
    </header>

    <!-- All transitions stacked -->
    <div class="max-w-screen-2xl mx-auto px-8">
      <div
        v-for="(t, i) in transitions"
        :key="i"
        :id="'transition-' + t.slug"
        class="ft-transition-block animate-on-scroll"
        :class="i < transitions.length - 1 ? 'ft-transition-divider' : 'pb-16'"
        :data-shift-label="t.label">

        <!-- Info row -->
        <div class="tw-flex flex-col lg:flex-row gap-12 items-start mb-8">
          <!-- Left: label + title + desc + stats -->
          <div class="flex-1">
            <div class="tw-flex items-center gap-3 mb-5">
              <span class="ft-num text-xs font-black text-primary-slate/20">{{ String(i + 1).padStart(2, '0') }}</span>
              <div class="tw-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase" :class="t.pillClass">
                <Icon :name="t.icon" size="12"/>
                {{ t.label }}
              </div>
            </div>
            <h2 class="text-3xl md:text-5xl font-bold text-primary-slate leading-tight mb-4">{{ t.title }}</h2>
            <p class="text-primary-slate/55 font-light leading-relaxed max-w-2xl text-lg">{{ t.description }}</p>
            <div class="tw-flex flex-wrap gap-10 mt-8">
              <div v-for="(stat, si) in t.stats" :key="si">
                <div class="text-3xl font-black text-primary-slate">{{ stat.value }}</div>
                <div class="text-xs text-primary-slate/50 font-medium mt-1">{{ stat.label }}</div>
              </div>
            </div>
          </div>
          <!-- Right: features + CTA -->
          <div class="lg:w-72 shrink-0 lg:pt-14">
            <div class="text-xs font-bold tracking-widest uppercase text-primary-slate/35 mb-3">What's mapped</div>
            <div class="tw-flex flex-wrap gap-2 mb-6">
              <span
                v-for="(f, fi) in t.features"
                :key="fi"
                class="text-xs font-semibold px-3 py-1.5 rounded-full"
                :class="t.pillClass">
                {{ f }}
              </span>
            </div>
            <button
              @click="onReadMore(t)"
              class="w-full tw-inline-flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold transition-all active:scale-95 group"
              :class="t.ctaBtnClass">
              Read more
              <Icon name="arrow-right" size="14" class="transition-transform group-hover:translate-x-1"/>
            </button>
          </div>
        </div>

        <!-- Map embed -->
        <div class="ft-map-wrap relative w-full rounded-2xl overflow-hidden border" :class="t.detailBorderClass" style="height:560px;">
          <iframe class="w-full h-full border-none" :src="t.mapUrl" allowfullscreen allow="geolocation"/>
          <!-- Coming soon overlay -->
          <div
            v-if="t.mapComingSoon"
            class="absolute inset-0 tw-flex flex-col items-center justify-center gap-5"
            style="backdrop-filter:blur(8px);background:rgba(255,255,255,0.7);">
            <div class="w-14 h-14 rounded-2xl tw-flex items-center justify-center" :class="t.iconBgActive">
              <Icon :name="t.icon" size="24" :class="t.iconColorActive"/>
            </div>
            <div class="text-center">
              <p class="font-bold text-primary-slate text-lg mb-2">{{ t.label }} map — coming soon</p>
              <p class="text-sm text-primary-slate/50 font-light max-w-xs leading-relaxed">
                We're building out this transition map. Be among the first to add your ecosystem.
              </p>
            </div>
            <button @click="onEarlyAccess(t.label)" class="px-8 py-3 rounded-full text-sm font-bold active:scale-95" :class="t.ctaBtnClass">
              Get early access
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Bottom CTA -->
    <section class="py-24 ft-cta-bg">
      <div class="max-w-3xl mx-auto px-8 text-center animate-on-scroll">
        <h2 class="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
          Not sure where you fit?
        </h2>
        <p class="text-white/60 text-xl font-light mb-10">
          Explore the full ecosystem or talk to our team — we'll help you find your place in the transition.
        </p>
        <div class="tw-flex flex-wrap justify-center gap-4">
          <button
            @click="$router.push('/dashboards/home')"
            class="ft-btn-green tw-inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-lg">
            Explore the full map
            <Icon name="arrow-right" size="16"/>
          </button>
          <button
            @click="goToRegistration"
            class="ft-btn-outline px-10 py-4 rounded-full font-bold text-lg">
            Add your ecosystem
          </button>
        </div>
      </div>
    </section>

    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import Icon from '../Icon/Icon.vue'
  import { createScrollDepthTracker, trackFoodleapCta, trackFoodleapImpression, trackFoodleapSection } from '@/util/useFoodleapTracking.js'

  // Replace individual map embed URLs once dedicated transition maps are created
  const MAP_URL = 'https://platform.foodleap.com/ecosystem-maps/1/embed?apiKey=ds_live_BYlfkEtvKnsWzpHeI1fo'

  export default defineComponent({
    name: 'StaticHomeFoodleapTransitions',
    components: { SimplifiedFooterFoodleapNew, Icon },

    data () {
      return {
        transitions: [
          {
            slug: 'protein',
            label: 'Protein',
            shortTitle: 'Novel & alternative proteins',
            title: 'Protein Transition',
            description: 'Explore the organisations, innovations and insights driving the shift towards a more diverse, resilient and sustainable protein system. Discover the ecosystem shaping one of the most significant transitions in the future of food.',
            icon: 'vial',
            stats: [
              { value: '1,248', label: 'organisations' },
              { value: '43', label: 'countries' },
              { value: '72', label: 'domains' },
            ],
            features: ['Alternative Proteins', 'Precision Fermentation', 'Biomass Fermentation', 'Protein Ingredients', 'Hybrid Products', 'Protein Crops', 'Food Processing Technologies'],
            mapUrl: 'https://platform.foodleap.com/ecosystem-maps/15/embed?apiKey=ds_live_LTesBOBYi8scwskp2etM',
            mapComingSoon: false,
            accentColor: 'text-purple-400',
            activeClass: 'border-purple-400 bg-purple-400/5',
            inactiveClass: 'border-brand-light-grey bg-white hover:border-purple-400/40 hover:bg-purple-400/5',
            iconBgActive: 'bg-purple-400/20',
            iconBgInactive: 'bg-brand-light-grey/60',
            iconColorActive: 'text-purple-400',
            iconColorInactive: 'text-primary-slate/40',
            headerBgClass: 'bg-purple-400/5',
            detailBorderClass: 'border-purple-400/20',
            pillClass: 'bg-purple-400/10 text-purple-400',
            ctaBtnClass: 'bg-purple-400 text-white hover:opacity-90',
          },
          {
            slug: 'circular',
            label: 'Circular',
            shortTitle: 'Upcycling & resource efficiency',
            title: 'Food Upcycling',
            description: 'Explore the organisations, innovations and insights helping food systems use resources more efficiently and create value from side streams. Discover the ecosystem driving the transition towards more circular and resource efficient food systems.',
            icon: 'sync',
            stats: [
              { value: '140+', label: 'organisations' },
              { value: '12', label: 'countries' },
              { value: '18', label: 'domains' },
            ],
            features: ['Upcycling', 'Side Stream Valorisation', 'Circular Ingredients', 'Waste Reduction', 'Resource Efficiency', 'Circular Supply Chains', 'Food Processing Technologies'],
            mapUrl: 'https://platform.foodleap.com/ecosystem-maps/4/embed?apiKey=ds_live_iM84TiIL8KwvRrsnZuKq',
            mapComingSoon: false,
            accentColor: 'text-secondary-blue',
            activeClass: 'border-secondary-blue bg-secondary-blue/5',
            inactiveClass: 'border-brand-light-grey bg-white hover:border-secondary-blue/40 hover:bg-secondary-blue/5',
            iconBgActive: 'bg-secondary-blue/20',
            iconBgInactive: 'bg-brand-light-grey/60',
            iconColorActive: 'text-secondary-blue',
            iconColorInactive: 'text-primary-slate/40',
            headerBgClass: 'bg-secondary-blue/5',
            detailBorderClass: 'border-secondary-blue/20',
            pillClass: 'bg-secondary-blue/10 text-secondary-blue',
            ctaBtnClass: 'bg-secondary-blue text-primary-slate hover:opacity-90',
          },
          {
            slug: 'food-infrastructure',
            label: 'Health',
            shortTitle: 'Nutrition, prevention & health',
            title: 'Health & Functional Food',
            description: 'Explore the organisations, innovations and insights at the intersection of food, nutrition and health. Discover the ecosystem contributing to healthier and more preventative food systems.',
            icon: 'building',
            stats: [
              { value: '120+', label: 'organisations' },
              { value: '14', label: 'countries' },
              { value: '16', label: 'domains' },
            ],
            features: ['Functional Ingredients', 'Personalised Nutrition', 'Gut Health & Microbiome', 'Metabolic Health', 'Healthy Ageing', 'Preventive Nutrition', 'Bioactives & Nutraceuticals'],
            mapUrl: 'https://platform.foodleap.com/ecosystem-maps/16/embed?apiKey=ds_live_LivhKbBN600tUdKAXqjj',
            mapComingSoon: false,
            accentColor: 'text-secondary-yellow',
            activeClass: 'border-secondary-yellow bg-secondary-yellow/5',
            inactiveClass: 'border-brand-light-grey bg-white hover:border-secondary-yellow/40 hover:bg-secondary-yellow/5',
            iconBgActive: 'bg-secondary-yellow/20',
            iconBgInactive: 'bg-brand-light-grey/60',
            iconColorActive: 'text-secondary-yellow',
            iconColorInactive: 'text-primary-slate/40',
            headerBgClass: 'bg-secondary-yellow/5',
            detailBorderClass: 'border-secondary-yellow/20',
            pillClass: 'bg-secondary-yellow/10 text-secondary-yellow',
            ctaBtnClass: 'bg-secondary-yellow text-primary-slate hover:opacity-90',
          },
          {
            slug: 'regenerative',
            label: 'Nature Positive',
            shortTitle: 'Soil, water & biodiversity',
            title: 'Nature Positive Food Systems',
            description: 'Explore the organisations, innovations and insights helping food systems contribute to healthy ecosystems. Discover the ecosystem driving the transition towards more regenerative and resilient food systems.',
            icon: 'eco',
            stats: [
              { value: '80+', label: 'organisations' },
              { value: '9', label: 'countries' },
              { value: '15', label: 'domains' },
            ],
            features: ['Regenerative Agriculture', 'Soil Health', 'Biodiversity', 'Water Stewardship', 'Carbon Farming', 'Agroecology', 'Nature-Based Solutions'],
            mapUrl: 'https://platform.foodleap.com/ecosystem-maps/17/embed?apiKey=ds_live_Ugbc2ehd630hfUnaUUGC',
            mapComingSoon: false,
            accentColor: 'text-primary-slate',
            activeClass: 'border-secondary-vibrant-green bg-secondary-vibrant-green/10',
            inactiveClass: 'border-brand-light-grey bg-white hover:border-secondary-vibrant-green/40 hover:bg-secondary-vibrant-green/5',
            iconBgActive: 'bg-secondary-vibrant-green/30',
            iconBgInactive: 'bg-brand-light-grey/60',
            iconColorActive: 'text-primary-slate',
            iconColorInactive: 'text-primary-slate/40',
            headerBgClass: 'bg-secondary-vibrant-green/10',
            detailBorderClass: 'border-secondary-vibrant-green/30',
            pillClass: 'bg-secondary-vibrant-green/20 text-primary-slate',
            ctaBtnClass: 'bg-secondary-vibrant-green text-primary-slate hover:opacity-90',
          },
        ],
      }
    },

    methods: {
      goToRegistration () {
        this.$router.push('/register')
      },
      onReadMore (t) {
        trackFoodleapCta('Transitions', 'Read more', t.label)
        this.$router.push('/transitions/' + t.slug)
      },
      onEarlyAccess (shiftLabel) {
        trackFoodleapCta('Transitions', 'Get early access', shiftLabel)
        this.goToRegistration()
      },
    },

    mounted () {
      const focus = this.$route.query.focus
      if (focus) {
        this.$nextTick(() => {
          setTimeout(() => {
            const el = document.getElementById('transition-' + focus)
            if (el) {
              const mainView = document.querySelector('.main-view')
              const offset = el.getBoundingClientRect().top + (mainView ? mainView.scrollTop : window.scrollY) - 80
              if (mainView) {
                mainView.scrollTo({ top: offset, behavior: 'smooth' })
              } else {
                window.scrollTo({ top: offset, behavior: 'smooth' })
              }
            }
          }, 350)
        })
      }

      this._scrollTracker = createScrollDepthTracker('Foodleap Transitions')
      this._scrollTracker.start()

      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
              this._observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.1 }
      )
      this.$el.querySelectorAll('.animate-on-scroll').forEach(el => {
        this._observer.observe(el)
      })

      // Track which transition blocks are actually seen (20% threshold = "engaged")
      this._shiftObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const label = entry.target.dataset.shiftLabel
              if (label) {
                trackFoodleapImpression('Foodleap Shift Block', label)
                trackFoodleapSection('Transitions', label)
              }
              this._shiftObserver.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.2 }
      )
      this.$el.querySelectorAll('.ft-transition-block').forEach(el => {
        this._shiftObserver.observe(el)
      })
    },

    beforeUnmount () {
      if (this._observer) this._observer.disconnect()
      if (this._shiftObserver) this._shiftObserver.disconnect()
      if (this._scrollTracker) this._scrollTracker.stop()
    },
  })
</script>

<style lang="scss" scoped>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .foodleap-transitions {
    font-family: 'Inter', sans-serif;
    background-color: #ffffff;
    color: #21244A;

    $ps: #21244A;
    $pg: #5CCC61;
    $blg: #D9E2DA;
    $yellow: #FFC83D;
    $blue: #70C1FF;
    $vgreen: #B5F167;

    // ── Scroll-reveal ──────────────────────────────────────────────────────────
    .animate-on-scroll {
      opacity: 0;
      transform: translateY(20px);
      transition: opacity 0.6s ease, transform 0.6s ease;

      &.is-visible {
        opacity: 1;
        transform: translateY(0);
      }

      &.delay-1 {
        transition-delay: 0.1s;
      }

      &.delay-2 {
        transition-delay: 0.2s;
      }
    }

    // ── Hero ───────────────────────────────────────────────────────────────────
    .ft-hero {
      background: linear-gradient(180deg, #f6f9f6 0%, #ffffff 100%);
    }

    .ft-badge {
      background-color: rgba(92, 204, 97, 0.12);
      color: $pg;
    }

    .ft-blob {
      background: $pg;
    }

    .ft-blob-2 {
      background: $blue;
    }

    // ── Stacked transition blocks ─────────────────────────────────────────────
    .ft-transition-block {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }

    .ft-transition-divider {
      border-bottom: 1px solid $blg;
    }

    .ft-num {
      font-size: 2rem;
      line-height: 1;
      font-family: 'Inter', sans-serif;
    }

    .ft-map-wrap {
      box-shadow: 0 8px 40px rgba(33, 36, 74, 0.06);
    }

    .text-primary-slate\/55 {
      color: rgba(33, 36, 74, 0.55);
    }

    .text-primary-slate\/35 {
      color: rgba(33, 36, 74, 0.35);
    }

    // ── Bottom CTA ────────────────────────────────────────────────────────────
    .ft-cta-bg {
      background-color: $ps;
    }

    .ft-btn-green {
      background-color: $pg;
      color: $ps;
      transition: opacity 0.2s;

      &:hover {
        opacity: 0.9;
      }

      svg {
        transition: transform 0.2s;
      }

      &:hover svg {
        transform: translateX(3px);
      }
    }

    .ft-btn-outline {
      border: 2px solid rgba(255, 255, 255, 0.25);
      color: rgba(255, 255, 255, 0.75);
      transition: all 0.2s;

      &:hover {
        border-color: rgba(255, 255, 255, 0.6);
        color: #ffffff;
      }
    }

    // ── Color utilities (scoped) ───────────────────────────────────────────────
    .text-primary-slate {
      color: $ps;
    }

    .text-primary-green {
      color: $pg;
    }

    .text-secondary-blue {
      color: $blue;
    }

    .text-secondary-yellow {
      color: $yellow;
    }

    .text-primary-slate\/40 {
      color: rgba(33, 36, 74, 0.4);
    }

    .text-primary-slate\/50 {
      color: rgba(33, 36, 74, 0.5);
    }

    .text-primary-slate\/60 {
      color: rgba(33, 36, 74, 0.6);
    }

    .bg-primary-green\/5 {
      background-color: rgba(92, 204, 97, 0.05);
    }

    .bg-primary-green\/10 {
      background-color: rgba(92, 204, 97, 0.10);
    }

    .bg-primary-green\/20 {
      background-color: rgba(92, 204, 97, 0.20);
    }

    .bg-secondary-blue {
      background-color: $blue;
    }

    .bg-secondary-blue\/5 {
      background-color: rgba(112, 193, 255, 0.05);
    }

    .bg-secondary-blue\/10 {
      background-color: rgba(112, 193, 255, 0.10);
    }

    .bg-secondary-blue\/20 {
      background-color: rgba(112, 193, 255, 0.20);
    }

    .bg-secondary-yellow {
      background-color: $yellow;
    }

    .bg-secondary-yellow\/5 {
      background-color: rgba(255, 200, 61, 0.05);
    }

    .bg-secondary-yellow\/10 {
      background-color: rgba(255, 200, 61, 0.10);
    }

    .bg-secondary-yellow\/20 {
      background-color: rgba(255, 200, 61, 0.20);
    }

    .bg-secondary-vibrant-green {
      background-color: $vgreen;
    }

    .bg-secondary-vibrant-green\/5 {
      background-color: rgba(181, 241, 103, 0.05);
    }

    .bg-secondary-vibrant-green\/10 {
      background-color: rgba(181, 241, 103, 0.10);
    }

    .bg-secondary-vibrant-green\/20 {
      background-color: rgba(181, 241, 103, 0.20);
    }

    .bg-secondary-vibrant-green\/30 {
      background-color: rgba(181, 241, 103, 0.30);
    }

    .bg-brand-light-grey\/20 {
      background-color: rgba(217, 226, 218, 0.20);
    }

    .bg-brand-light-grey\/60 {
      background-color: rgba(217, 226, 218, 0.60);
    }

    .border-primary-green {
      border-color: $pg;
    }

    .border-primary-green\/20 {
      border-color: rgba(92, 204, 97, 0.20);
    }

    .border-primary-green\/40 {
      border-color: rgba(92, 204, 97, 0.40);
    }

    .border-secondary-blue {
      border-color: $blue;
    }

    .border-secondary-blue\/20 {
      border-color: rgba(112, 193, 255, 0.20);
    }

    .border-secondary-blue\/40 {
      border-color: rgba(112, 193, 255, 0.40);
    }

    .border-secondary-yellow {
      border-color: $yellow;
    }

    .border-secondary-yellow\/20 {
      border-color: rgba(255, 200, 61, 0.20);
    }

    .border-secondary-yellow\/40 {
      border-color: rgba(255, 200, 61, 0.40);
    }

    .border-secondary-vibrant-green {
      border-color: $vgreen;
    }

    .border-secondary-vibrant-green\/30 {
      border-color: rgba(181, 241, 103, 0.30);
    }

    .border-secondary-vibrant-green\/40 {
      border-color: rgba(181, 241, 103, 0.40);
    }

    .border-brand-light-grey {
      border-color: $blg;
    }
  }
</style>
